import React, { useState } from 'react'
import { useIntl } from "react-intl";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Button, Modal } from "react-bootstrap";
import { BsFillExclamationTriangleFill } from "react-icons/bs";
import { IPartnerCancelAppointmentInput } from "../../Types";
import { IRootState } from "../../../../Cutlio.Providers/ApplicationState";
import { PrimaryButton } from "../../../../Cutlio.Common/Buttons/PrimaryButton";
import { useCancelAppointmentMutation } from "../../Mutations/CancelAppointmentMutation";
import { GET_APPOINTMENTS_QUERY } from "../../../Calendar/Queries/GetAppointmentsQuery";
import { useReservationSidebarContext } from "../../../../Cutlio.Providers/ReservationSidebarProvider/Context";
import { ICutlioError, useApolloError } from "../../../../Cutlio.Common/Hooks/Error/ApolloNetworkError";
import { AppointmentCheckbox } from "../AppointmentCheckbox";

interface Props {
  show: boolean
  onBackClicked: () => void
}


export const AppointmentCancelWarning = ({show, onBackClicked}: Props) => {
  const intl = useIntl()
  const {toggleSidebar} = useReservationSidebarContext()
  const [sendNotification, setSendNotification] = useState(true)
  const [notifyWaitListUsers, setNotifyWaitListUsers] = useState(true)
  const [cancelAppointment, cancelAppointmentOptions] = useCancelAppointmentMutation();
  const {id, date, locationId, recurring} = useSelector((state: IRootState) => state.appointment.request);
  const cancelError: ICutlioError = useApolloError(cancelAppointmentOptions.error, cancelAppointmentOptions.loading)

  function handleSubmit() {
    const refetchVariables = {locationId, selectedDate: date};
    const refetchQueries = [{query: GET_APPOINTMENTS_QUERY, variables: refetchVariables}]

    const cancelRequest: IPartnerCancelAppointmentInput = {id, notifyWaitListUsers, cancelSeries: false, sendNotification, sendSms: false}
    cancelAppointment({variables: {cancelRequest}, awaitRefetchQueries: true, refetchQueries}).then(handleSuccessCancel).catch()
  }

  function handleSuccessCancel() {
    toggleSidebar()
  }

  function toggleSendNotification() {
    setSendNotification(!sendNotification)
  }

  function toggleSNotifyWaitListUsers() {
    setNotifyWaitListUsers(!notifyWaitListUsers)
  }

  return (
    <Modal show={show} backdrop="static" keyboard={false}>
      <StyledContainer>
        <StyledWrapper>
          <StyledIcon size={45}/>
          <StyledContent>
            <StyledTitle>{intl.formatMessage({id: "appointment.details.actions.cancel.title"})}</StyledTitle>
            <StyledDescription>{intl.formatMessage({id: "appointment.details.actions.cancel.placeholder"})}</StyledDescription>
            <StyledOptionsHolder>
              <AppointmentCheckbox
                hidden={false}
                checked={sendNotification}
                toggleCheckbox={toggleSendNotification}
                label={intl.formatMessage({id: "appointment.details.actions.send.notification"})}
              />
              <AppointmentCheckbox
                hidden={false}
                checked={notifyWaitListUsers}
                toggleCheckbox={toggleSNotifyWaitListUsers}
                label={intl.formatMessage({id: "appointment.details.actions.notify.wait.list.users"})}
              />
              <AppointmentCheckbox
                hidden={!recurring}
                checked={sendNotification}
                toggleCheckbox={toggleSendNotification}
                label={intl.formatMessage({id: "appointment.details.actions.cancel.all.recurring"})}
              />
            </StyledOptionsHolder>
            <StyledError hidden={!cancelError.showError}>{cancelError.errorMessage}</StyledError>
          </StyledContent>
        </StyledWrapper>
      </StyledContainer>
      <StyledFooter>
        <Button className="me-2" size="sm" variant="outline" onClick={onBackClicked}>
          {intl.formatMessage({id: "common.form.elements.back"})}
        </Button>
        <PrimaryButton
          size="sm"
          variant="warning"
          onClick={handleSubmit}
          isBusy={cancelAppointmentOptions.loading}
          title={intl.formatMessage({id: "appointment.details.actions.cancel"})}
        />
      </StyledFooter>
    </Modal>
  )
}

const StyledContainer = styled(Modal.Body)`
`

const StyledWrapper = styled.div`
  display: flex;
`

const StyledIcon = styled(BsFillExclamationTriangleFill)`
  color: var(--falcon-warning);
`

const StyledContent = styled.div`
  width: 100%;
  padding-top: .5rem;
  padding-left: .75rem;
`

const StyledTitle = styled.h5`
  font-size: 1.2rem;
  font-weight: 600;
`

const StyledFooter = styled(Modal.Footer)`
  border-top: none;
  background: var(--falcon-light);
`

const StyledError = styled.p`
  font-weight: 600;
  font-size: .9rem;
  margin-top: .75rem;
  border-radius: .375rem;
  color: var(--falcon-danger);
`

const StyledDescription = styled.p`
  opacity: .8;
  font-size: .8rem;
  font-weight: 500;
  font-style: italic;
  color: var(--falcon-gray-600);
`

const StyledOptionsHolder = styled.div`
  display: flex;
  flex-direction: column;
`